import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  tap,
} from 'rxjs';
import {
  CellComponent,
  ColumnComponent,
  ColumnDefinition,
  Filter,
  FilterParams,
  FilterType,
  GroupArg,
  Options,
  RowComponent,
  Sorter,
  TabulatorFull as Tabulator,
} from 'tabulator-tables';
import {
  CellClickedEventDataInterface,
  ColumnCalcType,
  ColumnResizedEventDataInterface,
  DataTreeRowCollapsedEventDataInterface,
  DataTreeRowExpandedEventDataInterface,
  DownloadOptionsInterface,
  GroupHeaderType,
  GroupStartOpenType,
  HeaderClickedEventDataInterface,
  PersistenceModeType,
  PersistenceReaderFuncType,
  PersistenceType,
  PersistenceWriterFuncType,
  RowMovedEventDataInterface,
  RowSelectionChangedEventDataInterface,
  SelectableType,
  TableGridRowDataType,
  TableLayoutType,
  WindowInterface,
} from '../model';
import {
  DEFAULT_CHILDREN_FIELD_NAME,
  DEFAULT_COLUMN_CALC_TYPE,
  DEFAULT_DATA_TREE_CHILD_IDENT,
  DEFAULT_DATA_TREE_START_EXPANDED,
  DEFAULT_ROW_HEIGHT,
  DEFAULT_TABLE_LAYOUT_TYPE,
} from '../model/constants';
import { DataTreeStartExpandedType } from '../model/types/data-tree-start-expanded.type';
import { RowFormatterType } from '../model/types/row-formatter.type';
import { IIKOTableGridInputInterface } from './iiko-table-grid-input.interface';
import { IIKOTableGridOutputInterface } from './iiko-table-grid-output.interface';
import { DataFilteredEventDataInterface } from '../model/interfaces/data-filtered-event-data.interface';

@Component({
  selector: 'resto-table-grid',
  templateUrl: './iiko-table-grid.component.html',
  styleUrls: ['./iiko-table-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
})
export class IIKOTableGridComponent
  implements IIKOTableGridInputInterface, IIKOTableGridOutputInterface, OnInit, AfterViewInit
{

  @Output() tableInstanceChange = new EventEmitter();

  /** If noDataMessage is defined than empty table will be replaced with the text message.  */
  @Input() set noDataMessage(value: string | undefined) {
    this._noDataMessage = value;
  }

  get noDataMessage(): string | undefined {
    return this._noDataMessage;
  }

  /** - Sets the height of the containing element, can be set to any valid height css value. If set to false (the default), the height of the table will resize to fit the table data. */
  @Input() set height(value: string | undefined) {
    this._height = value;
    if (value) {
      this._tabulatorInstance?.setHeight(value);
    } else {
      this._recreateTabulatorInstance$$.next(undefined);
    }
  }

  get height(): string | undefined {
    return this._height;
  }

  /** Enable data reactivity.
   * You can enable reactive data by setting the reactiveData option to true in the table constructor,
   * and then passing your data array to the data option.
   * Once the table is built any changes to the array will automatically be replicated to the table
   * without needing to call any functions on the table itself. */
  @Input() set reactiveData(value: boolean) {
    this._reactiveData = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get reactiveData(): boolean {
    return this._reactiveData;
  }

  /** Define which table data should be persisted
   *
   * Tabulator can persist a variety of different table configuration options. By setting the persistence property to true the table will persist the sort, filter, headerFilter, group (groupBy, groupStartOpen, groupHeader), pagination (paginationSize, currently selected page), and column (title, width, visibility, order) configuration of the table
   *
   * var table = new Tabulator("#example-table", {
   *     persistence:true, //enable table persistence
   * });
   * For more granular control of what is persisted you can pass an object to the persistence property:
   *
   * var table = new Tabulator("#example-table", {
   *     persistence:{
   *         sort: true, //persist column sorting
   *         filter: true, //persist filters
   *         headerFilter: true, //persist header filters
   *         group: true, //persist row grouping
   *         page: true, //persist page
   *         columns: true, //persist columns
   *     }
   * });
   * */
  @Input() set persistence(value: PersistenceType | undefined) {
    this._persistence = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get persistence(): PersistenceType | undefined {
    return this._persistence;
  }

  /** Store persistence information in a cookie or localStorage
   *
   * Persistence information can either be stored in a cookie or in the localSotrage object, you can use the persistenceMode to choose which. It can take three possible values:
   *
   * local - (string) Store the persistence information in the localStorage object
   * cookie - (string) Store the persistence information in a cookie
   * true - (boolean) check if localStorage is available and store persistence information, otherwise store in cookie (Default option)
   * var table = new Tabulator("#example-table", {
   *     persistenceMode:"cookie", //store persistence information in a cookie
   * });
   * */
  @Input() set persistenceMode(value: PersistenceModeType) {
    this._persistenceMode = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get persistenceMode(): PersistenceModeType {
    return this._persistenceMode;
  }

  /** Override persistence reader functionality to read from custom package */
  @Input() set persistenceReaderFunc(value: PersistenceReaderFuncType | undefined) {
    this._persistenceReaderFunc = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get persistenceReaderFunc(): PersistenceReaderFuncType | undefined {
    return this._persistenceReaderFunc;
  }

  /** Override persistence writer functionality to write to custom package */
  @Input() set persistenceWriterFunc(value: PersistenceWriterFuncType | undefined) {
    this._persistenceWriterFunc = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get persistenceWriterFunc(): PersistenceWriterFuncType | undefined {
    return this._persistenceWriterFunc;
  }

  /** Boolean/function to set the open/closed state of groups when they are first created */
  @Input() set groupStartOpen(value: GroupStartOpenType) {
    this._groupStartOpen = value;

    this._recreateTabulatorInstance$$.next(undefined);
  }

  get groupStartOpen(): GroupStartOpenType {
    return this._groupStartOpen;
  }

  /** Show/hide column calculations when group is closed */
  @Input() set groupClosedShowCalcs(value: boolean) {
    this._groupClosedShowCalcs = value;

    this._recreateTabulatorInstance$$.next(undefined);
  }

  get groupClosedShowCalcs(): boolean {
    return this._groupClosedShowCalcs;
  }

  /** String/function to select field to group rows by */
  @Input() set groupBy(value: GroupArg | undefined) {
    this._groupBy = value;

    this._recreateTabulatorInstance$$.next(undefined);
  }

  get groupBy(): GroupArg | undefined {
    return this._groupBy;
  }

  /** Function to layout group header row */
  @Input() set groupHeader(value: GroupHeaderType | undefined) {
    this._groupHeader = value;

    this._recreateTabulatorInstance$$.next(undefined);
  }

  get groupHeader(): GroupHeaderType | undefined {
    return this._groupHeader;
  }

  /** Where to show column calcs in table
   *
   * By default column calculations are shown at the top and bottom of the table, unless row grouping is enabled, in which case they are shown at the top and bottom of each group.
   *
   * The columnCalcs option lets you decided where the calculations should be displayed, it can take one of four values:
   *
   * true - show calcs at top and bottom of the table, unless grouped, then show in groups (boolean, default)
   * both - show calcs at top and bottom of the table and show in groups
   * table - show calcs at top and bottom of the table only
   * group - show calcs in groups only
   * */
  @Input() set columnCalcs(value: ColumnCalcType) {
    this._columnCalcs = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get columnCalcs(): ColumnCalcType {
    return this._columnCalcs;
  }

  /** Disable column header bar */
  @Input() set headerVisible(value: boolean) {
    this._headerVisible = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get headerVisible(): boolean {
    return this._headerVisible;
  }

  /** Allow users to move and reorder columns */
  @Input() set movableColumns(value: boolean) {
    this._movableColumns = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get movableColumns(): boolean {
    return this._movableColumns;
  }

  /** Layout mode for the table columns */
  @Input() set layout(value: TableLayoutType | undefined) {
    this._layout = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get layout(): TableLayoutType | undefined {
    return this._layout;
  }

  /** The data field to look for child rows. The default value is "_children" */
  @Input() set dataTreeChildField(value: string) {
    this._dataTreeChildField = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get dataTreeChildField(): string {
    return this._dataTreeChildField;
  }

  /** The default expansion state for tree nodes */
  @Input() set dataTreeStartExpanded(value: DataTreeStartExpandedType) {
    this._dataTreeStartExpanded = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get dataTreeStartExpanded(): DataTreeStartExpandedType {
    return this._dataTreeStartExpanded;
  }

  /** Enable tree layout. For nested data sets Tabulator allows you to structure your rows as a tree,
   * allowing users to collapse and expand nested sets of rows. */
  @Input() set dataTree(value: boolean) {
    this._dataTree = value;

    this._recreateTabulatorInstance$$.next(undefined);
  }

  get dataTree(): boolean {
    return this._dataTree;
  }

  /** The headerSort option can now be set in the table options to affect all columns as well as in column definitions. */
  @Input() set headerSort(value: boolean) {
    this._headerSort = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get headerSort(): boolean {
    return this._headerSort;
  }

  /** Array of sorters to be applied on load.
   *
   * When the table is first created it can be defined with an initial set of sorters. These can be set using the initialSort option. This will take the same sorter array as the setSort function. (see Sorter Functions for more details)
   *
   * var table = new Tabulator("#example-table", {
   *     initialSort:[
   *         {column:"age", dir:"asc"}, //sort by this first
   *         {column:"height", dir:"desc"}, //then sort by this second
   *     ]
   * });
   * */
  @Input() set initialSort(value: Sorter[] | false) {
    this._initialSort = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get initialSort(): Sorter[] | false {
    return this._initialSort;
  }

  /** Set fixed height for rows */
  @Input() set rowHeight(value: number) {
    this._rowHeight = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get rowHeight(): number {
    return this._rowHeight;
  }

  /** Allow the user to move rows up and down the table */
  @Input() set movableRows(value: boolean) {
    this._movableRows = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get movableRows(): boolean {
    return this._movableRows;
  }

  /** Allow user to resize rows (via handles on the top and bottom edges of the row) */
  @Input() set resizableRows(value: boolean) {
    this._resizableRows = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get resizableRows(): boolean {
    return this._resizableRows;
  }

  /** Enable/Disable row selection
   *
   * To enable row selection, set the selectable option to true
   *
   * var table = new Tabulator("#example-table", {
   * 	selectable:true,
   * });
   * The selectable option can take one of a several values:
   *
   * false - selectable rows are disabled
   * true - selectable rows are enabled, and you can select as many as you want
   * integer - any integer value, this sets the maximum number of rows that can be selected (when the maximum number of selected rows is exceded, the first selected row will be deselected to allow the next row to be selected).
   * "highlight" (default) - rows have the same hover stylings as selectable rows but do not change state when clicked. This is great for when you want to show that a row is clickable but don't want it to be selectable.
   * Note: using the setData function will clear the currently selected rows.
   * */
  @Input() set selectable(value: SelectableType) {
    this._selectable = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get selectable(): SelectableType {
    return this._selectable;
  }

  /** Function to alter layout of rows
   *
   * Tabulator also allows you to define a row level formatter using the rowFormatter option.
   * this lets you alter each row of the table based on the data it contains.
   *
   * The function accepts one argument, the RowComponent for the row being formatted.
   *
   * The example below changes the background colour of a row to blue if the col value
   * for that row is "blue".
   *
   * var table = new Tabulator("#example-table", {
   *     rowFormatter:function(row){
   *         //row - row component
   *
   *         var data = row.getData();
   *
   *         if(data.col == "blue"){
   *             row.getElement().style.backgroundColor = "#1e3b20";
   *         }
   *     },
   * });
   * */
  @Input() set rowFormatter(value: RowFormatterType | undefined) {
    this._rowFormatter = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get rowFormatter(): RowFormatterType | undefined {
    return this._rowFormatter;
  }

  /** It is mainly used to prefix names of table settings, which is persisted in the browser local storage. */
  @Input() tableId!: string;

  /** Sets `expand-table` to the Tabulator table container */
  @Input() tableExpanded? = false;

  /** Array to hold data that should be loaded on table creation */
  @Input() set tableData(items: TableGridRowDataType[]) {
    this._tableData = items;

    this._onSetTableData$$.next(this._tableData);
  }

  get tableData(): TableGridRowDataType[] {
    return this._tableData;
  }

  /** A class or a set of classes applied to the Tabulator table container */
  @Input() set classList(value: string | string[]) {
    this._classList = value;
    this._recreateTabulatorInstance$$.next(undefined);
  }

  get classList(): string | string[] {
    return this._classList;
  }

  /** Holder for column definition array */
  @Input() set columnDefs(columnDefs: ColumnDefinition[]) {
    if (columnDefs) {
      this._columnDefs = columnDefs;
    } else {
      this._columnDefs = [];
    }

    this._onSetColumnDefs$$.next(this._columnDefs);

    this._tabulatorInstance?.clearHeaderFilter();
    this._tabulatorInstance?.setColumns(this._columnDefs);
  }

  get columnDefs(): ColumnDefinition[] {
    return this._columnDefs;
  }

  /** The columnResized event will be triggered when a column has been resized by the user. */
  @Output() columnResized = new EventEmitter<ColumnResizedEventDataInterface>();

  /** The cellClick event is triggered when a user left clicks on a cell. */
  @Output() cellClicked = new EventEmitter<CellClickedEventDataInterface>();

  /** The dataTreeRowExpanded callback is triggered when a row with child rows is expanded to reveal the children. */
  @Output() dataTreeRowExpanded = new EventEmitter<DataTreeRowExpandedEventDataInterface>();

  /** The dataTreeRowCollapsed callback is triggered when a row with child rows is collapsed to hide its children. */
  @Output() dataTreeRowCollapsed = new EventEmitter<DataTreeRowCollapsedEventDataInterface>();

  /** The dataFiltered callback is triggered when a filters were applied to table*/
  @Output() dataFiltered = new EventEmitter<DataFilteredEventDataInterface>();

  /** The headerClick event is triggered when a user left clicks on a column or group header. */
  @Output() headerClicked = new EventEmitter<HeaderClickedEventDataInterface>();

  /** Whenever the number of selected rows changes, through selection or deselection, the rowSelectionChanged
   * event is triggered. This passes an array of the data objects for each row in the order they were selected
   * as the first argument, and an array of row components for each of the rows in order of selection as
   * the second argument. */
  @Output() rowSelectionChanged = new EventEmitter<RowSelectionChangedEventDataInterface>();

  /** The rowMoved event will be triggered when a row has been successfully moved. */
  @Output() rowMoved = new EventEmitter<RowMovedEventDataInterface>();

  /** The tabulatorInitialized event will be triggered when tabulator instance is built. */
  @Output() tabulatorInitialized = new EventEmitter<boolean>();

  @Output() renderCompleted = new EventEmitter<boolean>();

  get tabulatorContainerId(): string {
    return `id-${this.tableId}`;
  }

  get tabulatorContainerForDownloadId(): string {
    return `id-${this.tableId}-for-download`;
  }

  get dataIsEmpty(): boolean {
    return !this.tableData || this.tableData.length === 0;
  }

  get onSetColumnDefs$(): Observable<ColumnDefinition[]> {
    return this._onSetColumnDefs$$.asObservable();
  }

  get dataChanged$(): Observable<TableGridRowDataType[]> {
    return this._dataChanged$$.asObservable();
  }

  get dataLoaded$(): Observable<TableGridRowDataType[]> {
    return this._dataLoaded$$.asObservable();
  }

  get renderCompleted$(): Observable<boolean> {
    return this._renderCompleted$$.asObservable();
  }

  destroyRef = inject(DestroyRef);

  private _tabulatorInitialized$$ = new BehaviorSubject(false);

  private _onSetColumnDefs$$ = new BehaviorSubject<ColumnDefinition[]>([]);

  private _onSetTableData$$ = new BehaviorSubject<TableGridRowDataType[]>([]);

  private _dataChanged$$ = new Subject<TableGridRowDataType[]>();

  private _dataLoaded$$ = new Subject<TableGridRowDataType[]>();

  private _renderCompleted$$ = new Subject<boolean>();

  private _recreateTabulatorInstance$$ = new Subject<void>();

  private _tabulatorInitialized$ = this._tabulatorInitialized$$.pipe(distinctUntilChanged());

  private _noDataMessage?: string;

  private _srcRowPosition?: number;

  private _classList: string | string[] = '';

  private _columnDefs: ColumnDefinition[] = [];

  private _tableData: TableGridRowDataType[] = [];

  private _tabulatorInstance: Tabulator | undefined;

  private _height?: string;

  private _reactiveData = false;

  private _persistence?: PersistenceType;

  private _persistenceMode: PersistenceModeType = false;

  private _persistenceReaderFunc?: PersistenceReaderFuncType;

  private _persistenceWriterFunc?: PersistenceWriterFuncType;

  private _groupStartOpen: GroupStartOpenType = false;

  private _groupClosedShowCalcs = false;

  private _groupBy?: GroupArg;

  private _groupHeader?: GroupHeaderType;

  private _columnCalcs: ColumnCalcType = DEFAULT_COLUMN_CALC_TYPE;

  private _headerVisible = true;

  private _movableColumns = false;

  private _layout: TableLayoutType = DEFAULT_TABLE_LAYOUT_TYPE;

  private _dataTreeChildField = DEFAULT_CHILDREN_FIELD_NAME;

  private _dataTreeStartExpanded: DataTreeStartExpandedType = DEFAULT_DATA_TREE_START_EXPANDED;

  private _dataTree = false;

  private _initialSort: Sorter[] | false = false;

  private _headerSort = false;

  private _rowHeight = DEFAULT_ROW_HEIGHT;

  private _movableRows = false;

  private _resizableRows = false;

  private _selectable: SelectableType = false;

  private _rowFormatter?: RowFormatterType;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  ngOnInit(): void {
    this.initSetDataLogic();

    this._tabulatorInitialized$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((x: boolean) => this.tabulatorInitialized.emit(x));

    this.renderCompleted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((x: boolean) => this.renderCompleted.emit(x));
  }

  ngAfterViewInit(): void {
    this._recreateTabulatorInstance$$
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.recreateTable());

    this._recreateTabulatorInstance$$.next(undefined);
  }

  setFilter(
    p1: string | Filter[] | unknown[] | ((data: unknown, filterParams: unknown) => boolean),
    p2?: FilterType | Record<string, unknown>,
    value?: unknown,
    filterParams?: FilterParams
  ): void {
    this._tabulatorInstance?.setFilter(p1, p2, value, filterParams);
  }

  clearFilter(includeHeaderFilters: boolean): void {
    this._tabulatorInstance?.clearFilter(includeHeaderFilters);
  }

  download(options: DownloadOptionsInterface): void {
    if (!this.isSheetJsXlsxModuleLoaded()) {
      console.error(
        'The XLSX downloader requires that the SheetJS Library be included on your site, ' +
          'this can be included with the following script tag!\r\n' +
          ' Add this script to the header of the index.html file: \r\n' +
          '<script type="text/javascript" src="https://oss.sheetjs.com/sheetjs/xlsx.full.min.js"></script>'
      );
      return;
    }

    if (!this._tabulatorInstance) {
      console.warn('An attempt to download the report before table initialization');
      return;
    }

    const downloadTableOptions = this.createTabulatorOptions();
    const data = this._tabulatorInstance.getData();

    for (const item of data) {
      if (item.productCategory === '') {
        item.productCategory = 'Untitled';
      }
    }

    downloadTableOptions.data = data;

    if (downloadTableOptions.dataTree) {
      downloadTableOptions.dataTreeStartExpanded = true;
    }

    if (downloadTableOptions.groupBy) {
      downloadTableOptions.groupStartOpen = true;
    }

    const tableForDownload = this.createTabulatorInstance(
      `#${this.tabulatorContainerForDownloadId}`,
      downloadTableOptions
    );

    tableForDownload.on('tableBuilt', () => {
      tableForDownload.download('xlsx', `${options.filename ?? 'data'}.xlsx`, {
        compress: false,
        sheetName: options.sheetName ?? 'Noname',
      });
      tableForDownload.destroy();
    });
  }

  private destroyTable(): void {
    if (this._tabulatorInstance) {
      this._tabulatorInitialized$$.next(false);
      this._tabulatorInstance.destroy();
      this._tabulatorInstance = undefined;
    }
  }

  public recreateTable(): void {
    this.destroyTable();

    const tableOptions = this.createTabulatorOptions();
    this._tabulatorInstance = this.createTabulatorInstance(
      `#${this.tabulatorContainerId}`,
      tableOptions
    );
    this.subscribeToTableEvents(this._tabulatorInstance);
  }

  private subscribeToTableEvents(table: Tabulator): void {
    table.on('columnResized', (column: ColumnComponent) => {
      this.tabulatorColumnResized(column);
    });

    table.on('cellTap', (e: UIEvent, cell: CellComponent) => {
      this.tabulatorCellClicked(e, cell);
    });

    table.on('cellClick', (e: UIEvent, cell: CellComponent) => {
      this.tabulatorCellClicked(e, cell);
    });

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    table.on('rowSelectionChanged', (data: TableGridRowDataType[], rows: RowComponent[]) => {
      this.tabulatorRowSelectionChanged(data, rows);
    });

    table.on('headerClick', (e: UIEvent, column: ColumnComponent) => {
      this.tabulatorHeaderClicked(e, column);
    });

    table.on('rowMoved', (rowComponent: RowComponent) => {
      const srcRowPos = this._srcRowPosition ?? -1;
      this.rowMoved.emit({
        srcIndex: srcRowPos - 1,
        dstIndex: (rowComponent.getPosition() as number) - 1,
        rowData: rowComponent.getData(),
      });
    });

    table.on('rowMoving', (rowComponent: RowComponent) => {
      this._srcRowPosition = rowComponent.getPosition() as number;
    });

    table.on('tableBuilt', () => {
      this._tabulatorInitialized$$.next(true);
    });

    table.on('dataChanged', (data: TableGridRowDataType[]) => {
      this._dataChanged$$.next(data);
    });

    table.on('dataLoaded', (data: TableGridRowDataType[]) => {
      this._dataLoaded$$.next(data);
    });

    table.on('renderComplete', () => {
      this._renderCompleted$$.next(true);
    });

    table.on('dataTreeRowExpanded', (row: RowComponent, level: number) => {
      this.dataTreeRowExpanded.emit({ row, dataTreeLevel: level });
    });

    table.on('dataTreeRowCollapsed', (row: RowComponent, level: number) => {
      this.dataTreeRowCollapsed.emit({ row, dataTreeLevel: level, level });
    });

    table.on('dataFiltered', (filters: Filter[], rows: RowComponent[]) => {
      this.dataFiltered.emit({ rows, filters });
    });
  }

  private tabulatorColumnResized(column: ColumnComponent): void {
    this.columnResized.emit({
      column,
    });
  }

  private tabulatorHeaderClicked(e: UIEvent, column: ColumnComponent): void {
    this.headerClicked.emit({
      e,
      column,
    });
  }

  private tabulatorRowSelectionChanged(data: TableGridRowDataType[], rows: RowComponent[]) {
    this.rowSelectionChanged.emit({
      data,
      rows,
    });
  }

  private tabulatorCellClicked(e: UIEvent, cell: CellComponent): void {
    let dataTreeLevel;
    if (this.dataTree) {
      dataTreeLevel = this.getRowItemLevel(cell.getData());
      if (dataTreeLevel === undefined) {
        throw Error(
          `The level of the clicked cell hasn't been matched. Cell data: ${JSON.stringify(
            cell.getData()
          )}`
        );
      }
    } else {
      dataTreeLevel = 0;
    }

    this.cellClicked.emit({
      e,
      fieldName: cell.getField(),
      cellValue: cell.getValue(),
      rowData: cell.getData(),
      cell,
      dataTreeLevel,
    });
  }

  private isSheetJsXlsxModuleLoaded(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(this.document.defaultView as any as WindowInterface).XLSX;
  }

  private createTabulatorOptions(): Options {
    const tableOptions: Options = {};

    this.setGeneralTableConfiguration(tableOptions);
    this.setPersistentConfiguration(tableOptions);
    this.setRowGroupingConfiguration(tableOptions);
    this.setColumnsConfiguration(tableOptions);
    this.setDataTreeConfiguration(tableOptions);
    this.setSortingConfiguration(tableOptions);
    this.setRowsConfiguration(tableOptions);

    tableOptions.data = this.tableData;
    tableOptions.columns = this.columnDefs;

    return tableOptions;
  }

  private setRowsConfiguration(tableOptions: Options) {
    tableOptions.rowHeight = this.rowHeight;

    if (this.movableRows) {
      tableOptions.movableRows = this.movableRows;
    }

    if (this._resizableRows) {
      tableOptions.resizableRows = this._resizableRows;
    }

    if (this._selectable !== false) {
      tableOptions.selectable = this._selectable;
    }

    if (this._rowFormatter) {
      tableOptions.rowFormatter = this._rowFormatter;
    }
  }

  private setSortingConfiguration(tableOptions: Options) {
    if (this._initialSort !== false) {
      tableOptions.initialSort = this._initialSort;
    }

    if (this._headerSort) {
      tableOptions.headerSort = this._headerSort;
    }
  }

  private setDataTreeConfiguration(tableOptions: Options) {
    if (this.dataTree) {
      const expandEl = document.createElement('mat-icon');
      expandEl.classList.add('expand_less');

      const collapsEl = document.createElement('mat-icon');
      collapsEl.classList.add('expand_more');

      tableOptions.dataTree = this.dataTree;

      tableOptions.dataTreeChildField = this._dataTreeChildField;
      tableOptions.dataTreeChildIndent = DEFAULT_DATA_TREE_CHILD_IDENT;
      tableOptions.dataTreeExpandElement = expandEl;
      tableOptions.dataTreeCollapseElement = collapsEl;

      if (this._dataTreeStartExpanded !== false) {
        tableOptions.dataTreeStartExpanded = this._dataTreeStartExpanded;
      }
    }
  }

  private setColumnsConfiguration(tableOptions: Options) {
    if (this.columnCalcs !== false) {
      tableOptions.columnCalcs = this.columnCalcs;
    }

    if (this.headerVisible) {
      tableOptions.headerVisible = this.headerVisible;
    }

    if (this.movableColumns) {
      tableOptions.movableColumns = this.movableColumns;
    }

    if (this.layout !== undefined) {
      tableOptions.layout = this.layout;
    }
  }

  private setRowGroupingConfiguration(tableOptions: Options) {
    if (this._groupStartOpen !== false) {
      tableOptions.groupStartOpen = this._groupStartOpen;
    }

    if (this._groupClosedShowCalcs) {
      tableOptions.groupClosedShowCalcs = this._groupClosedShowCalcs;
    }

    if (this.groupBy) {
      tableOptions.groupBy = this.groupBy;
    }

    if (this.groupHeader) {
      tableOptions.groupHeader = this.groupHeader;
    }
  }

  private setPersistentConfiguration(tableOptions: Options) {
    if (this.persistence) {
      tableOptions.persistence = this.persistence;
      if (this.persistenceMode !== false) {
        tableOptions.persistenceMode = this.persistenceMode;
      }
      if (this.persistenceReaderFunc) {
        tableOptions.persistenceReaderFunc = this.persistenceReaderFunc;
      }

      if (this.persistenceWriterFunc) {
        tableOptions.persistenceWriterFunc = this.persistenceWriterFunc;
      }
    }
  }

  private setGeneralTableConfiguration(tableOptions: Options) {
    if (this._height) {
      tableOptions.height = this._height;
    }

    if (this._reactiveData) {
      tableOptions.reactiveData = this._reactiveData;
    }
  }

  private initSetDataLogic() {
    combineLatest([this._onSetTableData$$, this._tabulatorInitialized$])
      .pipe(
        filter(([, initialized]: [TableGridRowDataType[], boolean]) => initialized),
        tap(([data]: [TableGridRowDataType[], boolean]) => {
          if (this._tableData) {
            this._tabulatorInstance?.setData(data).then(() => {
              this._tabulatorInstance?.redraw(true);
            });
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private createTabulatorInstance(id: string, options: Options): Tabulator {
    options.pagination = true
    const instance: Tabulator | undefined = new Tabulator(id, options);

    if (instance === undefined) {
      throw Error(`Can't accomplish instantiation. Tabulator instance is undefined.`);
    }

    this.tableInstanceChange.next(instance);

    return instance;
  }

  private getRowItemLevel(
    rowData: TableGridRowDataType,
    nodes: TableGridRowDataType[] = this.tableData,
    level = 0
  ): number | undefined {
    for (const node of nodes) {
      if (rowData === node) {
        return level;
      }
    }

    for (const node of nodes) {
      if (!Object.prototype.hasOwnProperty.call(node, this._dataTreeChildField)) {
        continue;
      }

      const childNodes = node[this._dataTreeChildField] as TableGridRowDataType[];
      if (!childNodes) {
        continue;
      }
      const matchedLevel = this.getRowItemLevel(rowData, childNodes, level + 1);
      if (matchedLevel !== undefined) {
        return matchedLevel;
      }
    }

    return undefined;
  }
}
